import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import SearchIcon from '@material-ui/icons/Search';
import SendIcon from '@material-ui/icons/Send';
import { makeStyles } from '@material-ui/core/styles';

import { explainFailure, renderTemplate } from '../../../constants/whatsapp';
import { actions as whatsappActions } from '../actions';
import useRenderedImage from '../hooks/useRenderedImage';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { toKyivWall } from '../kyivTime';

const POLL_MS = 8000;

function mapStateToProps(state) {
  return {
    ...state[WHATSAPP_NAME],
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  shell: {
    display: 'flex',
    height: 'calc(100vh - 190px)',
    minHeight: 460,
    borderRadius: 10,
    overflow: 'hidden',
  },
  list: {
    width: 340,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${colors.border}`,
  },
  search: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${colors.border}`,
  },
  items: {
    flexGrow: 1,
    overflowY: 'auto',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.border}`,
    '&:hover': { backgroundColor: colors.panelAlt },
  },
  itemActive: {
    backgroundColor: colors.accentSoft,
    '&:hover': { backgroundColor: colors.accentSoft },
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: colors.accent,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
  },
  itemBody: {
    minWidth: 0,
    flexGrow: 1,
  },
  itemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  itemName: {
    fontWeight: 600,
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemTime: {
    color: colors.textDim,
    fontSize: '11.5px',
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  itemBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  preview: {
    color: colors.textDim,
    fontSize: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    padding: '0 6px',
    marginLeft: theme.spacing(1),
    backgroundColor: colors.accent,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  thread: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ECE5DD',
  },
  threadHead: {
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: '#075E54',
    color: '#fff',
  },
  threadScroll: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(2, 3),
    display: 'flex',
    flexDirection: 'column',
  },
  day: {
    alignSelf: 'center',
    margin: theme.spacing(1.5, 0),
    padding: theme.spacing(0.25, 1.5),
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.8)',
    color: '#54656f',
    fontSize: '12px',
  },
  bubble: {
    maxWidth: '70%',
    padding: theme.spacing(1, 1.5, 0.5),
    borderRadius: 8,
    marginBottom: theme.spacing(0.75),
    boxShadow: '0 1px 1px rgba(0,0,0,0.12)',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  bubbleIn: {
    alignSelf: 'flex-start',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 0,
  },
  bubbleOut: {
    alignSelf: 'flex-end',
    backgroundColor: '#DCF8C6',
    borderTopRightRadius: 0,
  },
  bubbleWithImage: {
    width: 404,
    maxWidth: '70%',
  },
  threadImage: {
    display: 'block',
    width: '100%',
    borderRadius: 6,
    marginBottom: theme.spacing(0.75),
  },
  bubbleText: {
    fontSize: '14px',
    lineHeight: 1.45,
    color: '#111',
  },
  bubbleFooter: {
    display: 'block',
    marginTop: 4,
    fontSize: '12px',
    color: '#667781',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: '#667781',
    fontSize: '11px',
    marginTop: 2,
  },
  tick: {
    fontSize: '15px',
    marginLeft: 4,
  },
  failed: {
    color: '#d32f2f',
    fontSize: '12px',
    fontWeight: 600,
    marginTop: 4,
  },
  failedHint: {
    color: '#111',
    fontWeight: 400,
    marginTop: 2,
  },
  failedOriginal: {
    color: '#667781',
    fontWeight: 400,
    fontSize: '11px',
    marginTop: 2,
  },
  empty: {
    margin: 'auto',
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#54656f',
    maxWidth: 380,
  },
  reply: {
    borderTop: `1px solid ${colors.border}`,
    padding: theme.spacing(1.5, 2),
    backgroundColor: colors.panel,
  },
  replyChecking: {
    backgroundColor: colors.panelAlt,
    color: colors.textDim,
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    fontSize: '13px',
  },
  replyRow: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  replyNote: {
    marginTop: theme.spacing(0.75),
    fontSize: '12px',
    color: colors.textDim,
  },
  replyLocked: {
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    fontSize: '13px',
  },
  replyError: {
    marginTop: theme.spacing(0.75),
    fontSize: '12px',
    color: '#d32f2f',
  },
  note: {
    padding: theme.spacing(2),
    color: colors.textDim,
    fontSize: '13px',
  },
}));

// Почему писать нельзя: те же причины, что проверяет сервер.
const LOCKED_TEXT = {
  NO_INBOUND:
    'Клиент ещё не писал: свободным текстом ему можно ответить только после его сообщения. Отправьте шаблон (раздел «Рассылки») или дождитесь ответа клиента.',
  WINDOW_CLOSED:
    'С последнего сообщения клиента прошло больше 24 часов, и свободным текстом ему писать нельзя. Отправьте шаблон (раздел «Рассылки») или дождитесь ответа клиента: после него поле откроется снова.',
  MONTHLY_LIMIT:
    'Достигнут месячный лимит бесплатных ответов, дальше они платные. Отправка остановлена, шаблоны по-прежнему работают.',
  NOT_ALLOWED: 'Включён тестовый режим: этому номеру писать нельзя.',
};

const leftLabel = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  if (minutes > 0) return `${minutes} мин`;
  return 'меньше минуты';
};

const digits = (value) => String(value || '').replace(/\D/g, '');

const pad = (n) => `0${n}`.slice(-2);
const timeOf = (iso) => {
  const d = toKyivWall(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const dayOf = (iso) => {
  const d = toKyivWall(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const listTime = (iso) =>
  dayOf(iso) === dayOf(new Date().toISOString()) ? timeOf(iso) : dayOf(iso);

const initialsOf = (name) =>
  name
    .replace(/[^\p{L}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '#';

// Картинка отправленного шаблона. Её не хранят: рисуем заново из того же макета
// и тех же значений, получается та же картинка, что ушла клиенту.
function MessageImage({ template, params, className, onLoad }) {
  const values = {};
  params.forEach((value, index) => {
    values[String(index + 1)] = value;
  });
  const { url } = useRenderedImage(
    { template: { name: template.templateName, language: template.language }, values },
    0
  );
  return url ? <img src={url} alt="" className={className} onLoad={onLoad} /> : null;
}

function Ticks({ status, classes }) {
  if (status === 'read') {
    return <DoneAllIcon className={classes.tick} style={{ color: '#4FC3F7' }} />;
  }
  if (status === 'delivered') {
    return <DoneAllIcon className={classes.tick} />;
  }
  if (status === 'failed') {
    return <ErrorOutlineIcon className={classes.tick} style={{ color: '#d32f2f' }} />;
  }
  return <DoneIcon className={classes.tick} />;
}

// Поле ответа клиенту. Работает, пока открыто окно 24 часа с его последнего
// сообщения; когда оно закрывается (в том числе прямо на экране), поле блокируется.
function ReplyBar({ phone, state, onSend, classes }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const known = state.phone === phone;
  // «Прошло» не бывает отрицательным: таймер компонента может отстать от момента ответа
  // сервера, и тогда закрытое окно (0 секунд) на секунду выглядело бы открытым.
  const passed = known ? Math.max(0, Math.floor((now - state.receivedAt) / 1000)) : 0;
  const left = known ? Math.max(0, state.secondsLeft - passed) : 0;

  let locked = '';
  if (known && state.blocker === 'NOT_ALLOWED') locked = 'NOT_ALLOWED';
  else if (known && left <= 0) locked = state.blocker === 'NO_INBOUND' ? 'NO_INBOUND' : 'WINDOW_CLOSED';
  else if (known && state.limit > 0 && state.used >= state.limit) locked = 'MONTHLY_LIMIT';

  const max = (known && state.maxLength) || 4096;
  const canSend = known && !locked && !sending && text.trim().length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError('');
    const result = await onSend(phone, text.trim());
    setSending(false);
    if (result.ok) {
      setText('');
    } else {
      setError(result.error);
    }
  };

  // Пока сервер не сказал, можно ли отвечать, поле не показываем: ни активного, ни запертого.
  if (!known) {
    return (
      <div className={classes.reply}>
        <div className={classes.replyChecking}>Проверяем, можно ли отвечать…</div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className={classes.reply}>
        <div className={classes.replyLocked}>{LOCKED_TEXT[locked]}</div>
      </div>
    );
  }

  return (
    <div className={classes.reply}>
      <div className={classes.replyRow}>
        <TextField
          fullWidth
          multiline
          rowsMax={4}
          size="small"
          variant="outlined"
          placeholder="Ответ клиенту (Enter — отправить, Shift+Enter — новая строка)"
          value={text}
          disabled={sending}
          inputProps={{ maxLength: max }}
          onChange={(event) => {
            setText(event.target.value);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton
          color="primary"
          aria-label="Отправить"
          disabled={!canSend}
          onClick={handleSend}
        >
          <SendIcon />
        </IconButton>
      </div>
      {error && <div className={classes.replyError}>{error}</div>}
      <div className={classes.replyNote}>
        Можно отвечать ещё {leftLabel(left)} (до нового сообщения клиента).
        {state.limit > 0 && ` Ответов в этом месяце: ${state.used} из ${state.limit}.`}
        {text.length > max - 500 && ` Знаков: ${text.length} из ${max}.`}
      </div>
    </div>
  );
}

// Ответы клиентов: слева переписки, справа выбранная. Данные приходят от Meta
// через вебхук, экран только читает их из базы и обновляется раз в несколько секунд.
function Messages(props) {
  const classes = useStyles();
  const { conversations, conversationsLoaded, conversationsError, thread, templates, contacts } = props;

  const [activePhone, setActivePhone] = useState('');
  const [query, setQuery] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    props.fetchConversations();
    props.fetchTemplates();
    props.fetchCompanies();
    const timer = setInterval(() => {
      props.fetchConversations();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const contactByPhone = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[digits(contact.phone)] = contact;
    });
    return map;
  }, [contacts]);

  const nameOf = (conv) => {
    const known = contactByPhone[conv.phone];
    return (known && known.name) || conv.profileName || `+${conv.phone}`;
  };

  const active = conversations.find((item) => item.phone === activePhone);

  // Открытая переписка подтягивается сразу и затем вместе со списком.
  useEffect(() => {
    if (!activePhone) return undefined;
    props.fetchThread(activePhone);
    props.fetchReplyState(activePhone);
    const timer = setInterval(() => {
      props.fetchThread(activePhone);
      props.fetchReplyState(activePhone);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [activePhone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Пока переписка открыта, новые входящие сразу считаются прочитанными.
  useEffect(() => {
    if (active && active.unread > 0) {
      props.markConversationSeen(active.phone);
    }
  }, [active && active.unread, activePhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = thread.phone === activePhone ? thread.messages : [];

  // Переписка открывается в конце. Картинки шаблонов подгружаются позже и растягивают её,
  // поэтому, пока читатель не прокрутил вверх сам, после каждой загрузки снова идём вниз.
  const stickToEnd = useRef(true);
  const scrollToEnd = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };
  const handleThreadScroll = () => {
    const el = scrollRef.current;
    if (el) stickToEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  useEffect(() => {
    stickToEnd.current = true;
    scrollToEnd();
  }, [messages.length, activePhone]);

  // Если окно переписки меняет размер (например, поле ответа стало другой высоты), конец
  // остаётся на виду.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (stickToEnd.current) scrollToEnd();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activePhone]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (conv) =>
        nameOf(conv).toLowerCase().includes(q) || conv.phone.includes(digits(q) || q)
    );
  }, [conversations, query, contactByPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const templateOf = (name) => templates.find((item) => item.templateName === name);

  const textOf = (message) => {
    if (message.type !== 'template') return { text: message.body, footer: '' };
    const template = templateOf(message.templateName);
    if (!template) return { text: `[Шаблон ${message.templateName}]`, footer: '' };
    return {
      text: renderTemplate(template, message.templateParams || []),
      footer: template.footer,
    };
  };

  const previewOf = (conv) => {
    const { text } = textOf(conv.last);
    if (conv.last.status === 'failed') return 'Не доставлено';
    const prefix = conv.last.direction === 'out' ? 'Вы: ' : '';
    return `${prefix}${text}`;
  };

  return (
    <Paper className={classes.shell}>
      <div className={classes.list}>
        <div className={classes.search}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Имя или телефон"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="disabled" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className={classes.items}>
          {conversationsError && (
            <div className={classes.note} style={{ color: '#d32f2f' }}>
              {conversationsError}
            </div>
          )}
          {conversationsLoaded && !conversationsError && conversations.length === 0 && (
            <div className={classes.note}>
              Переписок пока нет. Здесь появятся рассылки, которые вы отправили, и
              ответы клиентов на них.
            </div>
          )}
          {visible.map((conv) => (
            <div
              key={conv.phone}
              role="button"
              tabIndex={0}
              className={`${classes.item} ${
                conv.phone === activePhone ? classes.itemActive : ''
              }`}
              onClick={() => setActivePhone(conv.phone)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setActivePhone(conv.phone);
              }}
            >
              <div className={classes.avatar}>{initialsOf(nameOf(conv))}</div>
              <div className={classes.itemBody}>
                <div className={classes.itemTop}>
                  <span className={classes.itemName}>{nameOf(conv)}</span>
                  <span className={classes.itemTime}>{listTime(conv.last.createdAt)}</span>
                </div>
                <div className={classes.itemBottom}>
                  <span className={classes.preview}>{previewOf(conv)}</span>
                  {conv.unread > 0 && <span className={classes.badge}>{conv.unread}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes.thread}>
        {!active && (
          <div className={classes.empty}>
            <Typography variant="body1">
              Выберите переписку слева, чтобы увидеть сообщения и ответы клиента.
            </Typography>
          </div>
        )}
        {active && (
          <>
            <div className={classes.threadHead}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {nameOf(active)}
              </Typography>
              <Typography variant="caption">+{active.phone}</Typography>
              {contactByPhone[active.phone] && (
                <Chip
                  size="small"
                  label={contactByPhone[active.phone].company}
                  style={{ marginLeft: 12, backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                />
              )}
            </div>
            <div className={classes.threadScroll} ref={scrollRef} onScroll={handleThreadScroll}>
              {messages.map((message, index) => {
                const previous = messages[index - 1];
                const newDay = !previous || dayOf(previous.createdAt) !== dayOf(message.createdAt);
                const { text, footer } = textOf(message);
                const outgoing = message.direction === 'out';
                const sentTemplate = outgoing && message.type === 'template' ? templateOf(message.templateName) : null;
                const withImage = Boolean(sentTemplate && sentTemplate.hasImage);
                return (
                  <React.Fragment key={message.id}>
                    {newDay && <span className={classes.day}>{dayOf(message.createdAt)}</span>}
                    <div
                      className={`${classes.bubble} ${
                        outgoing ? classes.bubbleOut : classes.bubbleIn
                      } ${withImage ? classes.bubbleWithImage : ''}`}
                    >
                      {withImage && (
                        <MessageImage
                          template={sentTemplate}
                          params={message.templateParams || []}
                          className={classes.threadImage}
                          onLoad={() => stickToEnd.current && scrollToEnd()}
                        />
                      )}
                      <span className={classes.bubbleText}>{text}</span>
                      {footer && <span className={classes.bubbleFooter}>{footer}</span>}
                      <div className={classes.meta}>
                        {timeOf(message.createdAt)}
                        {outgoing && <Ticks status={message.status} classes={classes} />}
                      </div>
                      {message.status === 'failed' && (
                        <div className={classes.failed}>
                          {explainFailure(message.error).title}
                          {explainFailure(message.error).hint && (
                            <div className={classes.failedHint}>
                              {explainFailure(message.error).hint}
                            </div>
                          )}
                          {explainFailure(message.error).original && (
                            <div className={classes.failedOriginal}>
                              Ответ Meta: {explainFailure(message.error).original}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <ReplyBar
              key={activePhone}
              phone={activePhone}
              state={props.replyState}
              classes={classes}
              onSend={async (phone, text) => {
                const result = await props.sendReply(phone, text);
                if (result.ok) props.fetchThread(phone);
                return result;
              }}
            />
          </>
        )}
      </div>
    </Paper>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Messages);
